// src/pages/caisse/StatsCaisse.jsx
import React, { useState, useEffect, useMemo } from "react";
import { 
    Card, Row, Col, Button, Spinner, Alert, Table, Badge 
} from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { 
    Chart as ChartJS, 
    CategoryScale, 
    LinearScale, 
    BarElement, 
    Title, 
    Tooltip, 
    Legend, 
    ArcElement,
    PointElement,
    LineElement
} from 'chart.js';
import { Bar, Pie } from 'react-chartjs-2';
import Footer from "../../layouts/Footer";
import Header from "../../layouts/Header";
import { formatCurrency, formatDate } from "../../utils/formatters";
import { API_BASE_URL } from "../../constants/api";

ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
    ArcElement,
    PointElement,
    LineElement
);

export default function StatsCaisse() {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [stats, setStats] = useState(null);
    const [operations, setOperations] = useState([]);
    const [period, setPeriod] = useState('month');

    useEffect(() => {
        fetchStats();
        fetchOperations();
    }, []);

    const fetchStats = async () => {
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(`${API_BASE_URL}/caisse.php?action=stats`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            
            if (!response.ok) throw new Error("Erreur chargement stats");
            
            const result = await response.json();
            setStats(result.data);
        } catch (err) {
            setError(err.message);
        }
    };

    const fetchOperations = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem("token");
            const response = await fetch(`${API_BASE_URL}/caisse.php`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            
            if (!response.ok) throw new Error("Erreur chargement");
            
            const result = await response.json();
            // Ne prendre que les opérations initiales pour les stats
            const initialOps = (result.data || []).filter(op => op.type === 'initial' || !op.parent_id);
            setOperations(initialOps);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    // Filtrer les opérations par période
    const filteredOps = useMemo(() => {
        const now = new Date();
        const currentYear = now.getFullYear();
        const currentMonth = now.getMonth();
        
        return operations.filter(op => {
            const opDate = new Date(op.created_at);
            if (period === 'month') {
                return opDate.getMonth() === currentMonth && opDate.getFullYear() === currentYear;
            } else if (period === 'year') {
                return opDate.getFullYear() === currentYear;
            }
            return true;
        });
    }, [operations, period]);

    // Calcul correct des totaux (utiliser montant_initial pour le total)
    const totalMontant = useMemo(() => filteredOps.reduce((sum, op) => sum + (parseFloat(op.montant_initial) || 0), 0), [filteredOps]);
    const totalAvance = useMemo(() => filteredOps.reduce((sum, op) => sum + (parseFloat(op.avance) || 0), 0), [filteredOps]);
    const totalReste = useMemo(() => filteredOps.reduce((sum, op) => sum + (parseFloat(op.reste) || 0), 0), [filteredOps]);
    
    const totalRegle = totalMontant - totalReste;
    
    const nbOperations = filteredOps.length;
    const nbSoldes = filteredOps.filter(op => (parseFloat(op.reste) || 0) <= 0).length;
    const nbImpayes = filteredOps.filter(op => (parseFloat(op.reste) || 0) > 0 && (parseFloat(op.avance) || 0) === 0).length;
    const nbPartiels = filteredOps.filter(op => (parseFloat(op.avance) || 0) > 0 && (parseFloat(op.reste) || 0) > 0).length;

    const pourcentageAvance = totalMontant > 0 ? (totalAvance / totalMontant) * 100 : 0;
    const tauxRecouvrement = totalMontant > 0 ? (totalRegle / totalMontant) * 100 : 0;

    // Données pour le graphique des montants par mois
    const montantsParMois = useMemo(() => {
        const moisMap = {};
        operations.forEach(op => {
            const date = new Date(op.created_at);
            const moisKey = `${date.getFullYear()}-${date.getMonth() + 1}`;
            const moisNom = date.toLocaleString('fr-FR', { month: 'long', year: 'numeric' });
            
            if (!moisMap[moisKey]) {
                moisMap[moisKey] = { mois: moisNom, total: 0, avance: 0, reste: 0, count: 0 };
            }
            moisMap[moisKey].total += parseFloat(op.montant_initial) || 0;
            moisMap[moisKey].avance += parseFloat(op.avance) || 0;
            moisMap[moisKey].reste += parseFloat(op.reste) || 0;
            moisMap[moisKey].count++;
        });
        
        const sorted = Object.entries(moisMap)
            .sort((a, b) => a[0].localeCompare(b[0]))
            .slice(-6);
        
        return {
            labels: sorted.map(([_, data]) => data.mois),
            datasets: [
                {
                    label: 'Montant total',
                    data: sorted.map(([_, data]) => data.total),
                    backgroundColor: 'rgba(33, 172, 205, 0.7)',
                    borderColor: '#21ACCD',
                    borderWidth: 1,
                    borderRadius: 6,
                },
                {
                    label: 'Reste à payer',
                    data: sorted.map(([_, data]) => data.reste),
                    backgroundColor: 'rgba(239, 68, 68, 0.7)',
                    borderColor: '#ef4444',
                    borderWidth: 1,
                    borderRadius: 6,
                },
                {
                    label: 'Avance initiale',
                    data: sorted.map(([_, data]) => data.avance),
                    backgroundColor: 'rgba(16, 185, 129, 0.7)',
                    borderColor: '#10b981',
                    borderWidth: 1,
                    borderRadius: 6,
                }
            ]
        };
    }, [operations]);

    const repartitionStats = useMemo(() => {
        return {
            labels: ['Soldé', 'Avance partielle', 'Impayé'],
            datasets: [
                {
                    data: [nbSoldes, nbPartiels, nbImpayes],
                    backgroundColor: ['#10b981', '#f59e0b', '#ef4444'],
                    borderWidth: 0,
                }
            ]
        };
    }, [nbSoldes, nbPartiels, nbImpayes]);

    const topBeneficiaires = useMemo(() => {
        const benefMap = {};
        filteredOps.forEach(op => {
            const nom = `${op.beneficiaire_prenom} ${op.beneficiaire_nom}`;
            if (!benefMap[nom]) {
                benefMap[nom] = { total: 0, avance: 0, reste: 0, count: 0 };
            }
            benefMap[nom].total += parseFloat(op.montant_initial) || 0;
            benefMap[nom].avance += parseFloat(op.avance) || 0;
            benefMap[nom].reste += parseFloat(op.reste) || 0;
            benefMap[nom].count++;
        });
        
        const sorted = Object.entries(benefMap)
            .sort((a, b) => b[1].total - a[1].total)
            .slice(0, 5);
        
        return {
            labels: sorted.map(([nom]) => nom.length > 20 ? nom.substring(0, 20) + '...' : nom),
            datasets: [
                {
                    label: 'Montant total',
                    data: sorted.map(([_, data]) => data.total),
                    backgroundColor: 'rgba(33, 172, 205, 0.7)',
                    borderColor: '#21ACCD',
                    borderWidth: 1,
                    borderRadius: 6,
                }
            ]
        };
    }, [filteredOps]);

    const barOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: { position: 'top' },
            tooltip: {
                callbacks: {
                    label: function(context) {
                        let label = context.dataset.label || '';
                        if (label) label += ': ';
                        label += new Intl.NumberFormat('fr-FR').format(context.raw) + ' FCFA';
                        return label;
                    }
                }
            }
        },
        scales: {
            y: {
                ticks: {
                    callback: function(value) {
                        return new Intl.NumberFormat('fr-FR').format(value) + ' FCFA';
                    }
                }
            }
        }
    };

    const pieOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: { position: 'bottom' },
            tooltip: {
                callbacks: {
                    label: function(context) {
                        const label = context.label || '';
                        const value = context.raw || 0;
                        const total = context.dataset.data.reduce((a, b) => a + b, 0);
                        const percentage = total > 0 ? Math.round((value / total) * 100) : 0;
                        return `${label}: ${value} opération(s) (${percentage}%)`;
                    }
                }
            }
        }
    };

    if (loading && operations.length === 0) {
        return (
            <>
                <Header />
                <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>
                    <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 'calc(100vh - 100px)' }}>
                        <div className="text-center">
                            <div className="spinner-border text-primary mb-3" style={{ width: '3rem', height: '3rem', color: '#21ACCD' }} role="status">
                                <span className="visually-hidden">Chargement...</span>
                            </div>
                            <h5 className="text-muted">Chargement des statistiques...</h5>
                        </div>
                    </div>
                </div>
                <Footer />
            </>
        );
    }

    return (
        <>
            <Header />
            <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>
                <div className="container-fluid">
                    {/* En-tête */}
                    <div className="d-md-flex align-items-center justify-content-between mb-4">
                        <div>
                            <div className="d-flex align-items-center gap-2 mb-2">
                                <div className="p-2 bg-primary bg-opacity-10 rounded-3">
                                    <i className="ri-bar-chart-2-line fs-20 text-primary" style={{ color: '#21ACCD' }}></i>
                                </div>
                                <div>
                                    <h4 className="main-title mb-0 fw-bold">Statistiques de caisse</h4>
                                    <p className="text-muted small mb-0">Analyse des décaissements</p>
                                </div>
                            </div>
                        </div>
                        <div className="d-flex gap-2 mt-3 mt-md-0">
                            <div className="btn-group" role="group">
                                <Button 
                                    variant={period === 'month' ? 'primary' : 'outline-secondary'}
                                    size="sm"
                                    onClick={() => setPeriod('month')}
                                    className="rounded-start-pill"
                                    style={period === 'month' ? { background: '#21ACCD', borderColor: '#21ACCD' } : {}}
                                >
                                    <i className="ri-calendar-line me-1"></i> Ce mois
                                </Button>
                                <Button 
                                    variant={period === 'year' ? 'primary' : 'outline-secondary'}
                                    size="sm"
                                    onClick={() => setPeriod('year')}
                                    style={period === 'year' ? { background: '#21ACCD', borderColor: '#21ACCD' } : {}}
                                >
                                    <i className="ri-calendar-2-line me-1"></i> Cette année
                                </Button>
                                <Button 
                                    variant={period === 'all' ? 'primary' : 'outline-secondary'}
                                    size="sm"
                                    onClick={() => setPeriod('all')}
                                    className="rounded-end-pill"
                                    style={period === 'all' ? { background: '#21ACCD', borderColor: '#21ACCD' } : {}}
                                >
                                    <i className="ri-stack-line me-1"></i> Tout
                                </Button>
                            </div>
                            <Button 
                                variant="outline-secondary" 
                                size="sm"
                                onClick={() => navigate('/caisse/operations')}
                                className="rounded-pill px-3 d-flex align-items-center gap-1"
                            >
                                <i className="ri-history-line"></i> Historique
                            </Button>
                        </div>
                    </div>

                    {/* KPI Cards */}
                    <Row className="g-3 mb-4">
                        <Col sm="6" xl="3">
                            <div className="kpi-card kpi-primary">
                                <div className="kpi-icon"><i className="ri-bank-card-line fs-24"></i></div>
                                <div className="kpi-content">
                                    <span className="kpi-label">Total décaissements</span>
                                    <h3 className="kpi-value">{formatCurrency(totalMontant)}</h3>
                                    <span className="kpi-unit">{nbOperations} opération(s)</span>
                                </div>
                            </div>
                        </Col>
                        <Col sm="6" xl="3">
                            <div className="kpi-card kpi-success">
                                <div className="kpi-icon"><i className="ri-hand-coin-line fs-24"></i></div>
                                <div className="kpi-content">
                                    <span className="kpi-label">Total réglé</span>
                                    <h3 className="kpi-value text-success">{formatCurrency(totalRegle)}</h3>
                                    <span className="kpi-unit">{tauxRecouvrement.toFixed(1)}% du total</span>
                                </div>
                            </div>
                        </Col>
                        <Col sm="6" xl="3">
                            <div className="kpi-card kpi-danger">
                                <div className="kpi-icon"><i className="ri-bank-card-line fs-24"></i></div>
                                <div className="kpi-content">
                                    <span className="kpi-label">Reste à payer</span>
                                    <h3 className="kpi-value text-danger">{formatCurrency(totalReste)}</h3>
                                    <span className="kpi-unit">Montant impayé</span>
                                </div>
                            </div>
                        </Col>
                        <Col sm="6" xl="3">
                            <div className="kpi-card kpi-warning">
                                <div className="kpi-icon"><i className="ri-percent-line fs-24"></i></div>
                                <div className="kpi-content">
                                    <span className="kpi-label">Avances initiales</span>
                                    <h3 className="kpi-value text-warning">{formatCurrency(totalAvance)}</h3>
                                    <span className="kpi-unit">{pourcentageAvance.toFixed(1)}% du total</span>
                                </div>
                            </div>
                        </Col>
                    </Row>

                    {/* Statistiques supplémentaires */}
                    <Row className="g-3 mb-4">
                        <Col sm="4">
                            <Card className="border-0 shadow-sm rounded-3 text-center">
                                <Card.Body className="p-3">
                                    <div className="small-stats-icon mb-2">
                                        <i className="ri-check-double-line text-success fs-3"></i>
                                    </div>
                                    <h2 className="mb-0 fw-bold text-success">{nbSoldes}</h2>
                                    <small className="text-muted">Opérations soldées</small>
                                </Card.Body>
                            </Card>
                        </Col>
                        <Col sm="4">
                            <Card className="border-0 shadow-sm rounded-3 text-center">
                                <Card.Body className="p-3">
                                    <div className="small-stats-icon mb-2">
                                        <i className="ri-time-line text-warning fs-3"></i>
                                    </div>
                                    <h2 className="mb-0 fw-bold text-warning">{nbPartiels}</h2>
                                    <small className="text-muted">Avances partielles</small>
                                </Card.Body>
                            </Card>
                        </Col>
                        <Col sm="4">
                            <Card className="border-0 shadow-sm rounded-3 text-center">
                                <Card.Body className="p-3">
                                    <div className="small-stats-icon mb-2">
                                        <i className="ri-error-warning-line text-danger fs-3"></i>
                                    </div>
                                    <h2 className="mb-0 fw-bold text-danger">{nbImpayes}</h2>
                                    <small className="text-muted">Opérations impayées</small>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>

                    {/* Graphiques */}
                    <Row className="g-4">
                        <Col lg={7}>
                            <Card className="border-0 shadow-sm rounded-3 h-100">
                                <Card.Header className="bg-transparent border-0 pt-4">
                                    <h6 className="mb-0 fw-semibold">
                                        <i className="ri-bar-chart-line me-2" style={{ color: '#21ACCD' }}></i>
                                        Évolution mensuelle des décaissements
                                    </h6>
                                </Card.Header>
                                <Card.Body>
                                    <div style={{ height: '350px' }}>
                                        <Bar data={montantsParMois} options={barOptions} />
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                        <Col lg={5}>
                            <Card className="border-0 shadow-sm rounded-3 h-100">
                                <Card.Header className="bg-transparent border-0 pt-4">
                                    <h6 className="mb-0 fw-semibold">
                                        <i className="ri-pie-chart-line me-2" style={{ color: '#21ACCD' }}></i>
                                        Répartition par statut
                                    </h6>
                                </Card.Header>
                                <Card.Body>
                                    <div style={{ height: '300px' }}>
                                        <Pie data={repartitionStats} options={pieOptions} />
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>

                    <Row className="g-4 mt-2">
                        <Col lg={12}>
                            <Card className="border-0 shadow-sm rounded-3">
                                <Card.Header className="bg-transparent border-0 pt-4">
                                    <h6 className="mb-0 fw-semibold">
                                        <i className="ri-user-star-line me-2" style={{ color: '#21ACCD' }}></i>
                                        Top 5 bénéficiaires ({period === 'month' ? 'ce mois' : period === 'year' ? 'cette année' : 'toutes opérations'})
                                    </h6>
                                </Card.Header>
                                <Card.Body>
                                    <div style={{ height: '350px' }}>
                                        <Bar data={topBeneficiaires} options={barOptions} />
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>

                    {/* Dernières opérations */}
                    {/* <Row className="g-4 mt-2">
                        <Col lg={12}>
                            <Card className="border-0 shadow-sm rounded-3">
                                <Card.Header className="bg-transparent border-0 pt-4">
                                    <h6 className="mb-0 fw-semibold">
                                        <i className="ri-history-line me-2" style={{ color: '#21ACCD' }}></i>
                                        Dernières opérations ({period === 'month' ? 'ce mois' : period === 'year' ? 'cette année' : 'toutes'})
                                    </h6>
                                </Card.Header>
                                <Card.Body className="p-0">
                                    <div className="table-responsive">
                                        <Table hover className="align-middle mb-0">
                                            <thead className="bg-light">
                                                <tr>
                                                    <th className="ps-4">Bordereau</th>
                                                    <th>Bénéficiaire</th>
                                                    <th>Motif</th>
                                                    <th className="text-end">Montant</th>
                                                    <th className="text-end">Avance</th>
                                                    <th className="text-end">Reste</th>
                                                    <th>Date</th>
                                                    <th className="text-center pe-4">Statut</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {filteredOps.slice(0, 10).map((op, idx) => {
                                                    const resteOp = (parseFloat(op.montant_initial) || 0) - (parseFloat(op.avance) || 0);
                                                    let statut = '', statutColor = '';
                                                    if (resteOp <= 0) { statut = 'Soldé'; statutColor = '#10b981'; }
                                                    else if ((parseFloat(op.avance) || 0) > 0) { statut = 'Partiel'; statutColor = '#f59e0b'; }
                                                    else { statut = 'Impayé'; statutColor = '#ef4444'; }
                                                    return (
                                                        <tr key={idx}>
                                                            <td className="ps-4"><span className="reference-badge">{op.numero_bordereau}</span></td>
                                                            <td><div className="fw-semibold">{op.beneficiaire_prenom} {op.beneficiaire_nom}</div></td>
                                                            <td><small className="text-muted">{op.motif?.substring(0, 40)}{op.motif?.length > 40 ? '...' : ''}</small></td>
                                                            <td className="text-end fw-semibold">{formatCurrency(op.montant_initial)}</td>
                                                            <td className="text-end">{formatCurrency(op.avance || 0)}</td>
                                                            <td className="text-end"><span className={resteOp > 0 ? 'text-danger' : 'text-success'}>{formatCurrency(resteOp)}</span></td>
                                                            <td><div className="small">{formatDate(op.created_at)}</div><small className="text-muted">{new Date(op.created_at).toLocaleTimeString('fr-FR')}</small></td>
                                                            <td className="text-center pe-4"><span className="status-badge" style={{ background: statutColor, color: 'white' }}>{statut}</span></td>
                                                        </tr>
                                                    );
                                                })}
                                                {filteredOps.length === 0 && (
                                                    <tr>
                                                        <td colSpan="8" className="text-center py-5">
                                                            <i className="ri-inbox-line fs-48 text-muted mb-3 d-block"></i>
                                                            <h6 className="mb-1">Aucune opération</h6>
                                                            <p className="text-muted small mb-0">Aucune opération pour la période sélectionnée</p>
                                                        </td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </Table>
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row> */}
                </div>
                <Footer />
            </div>

            <style>{`
                .kpi-card { background: white; border-radius: 0px; padding: 1rem; display: flex; align-items: center; gap: 1rem; box-shadow: 0 1px 3px rgba(0,0,0,0.05); transition: all 0.2s ease; height: 100%; border-left: 2px solid; }
                .kpi-card:hover { transform: translateY(-2px); box-shadow: 0 8px 20px rgba(0,0,0,0.08); }
                .kpi-primary { border-left-color: #21ACCD; } .kpi-success { border-left-color: #10b981; } .kpi-danger { border-left-color: #ef4444; } .kpi-warning { border-left-color: #f59e0b; }
                .kpi-icon { width: 48px; height: 48px; border-radius: 12px; display: flex; align-items: center; justify-content: center; }
                .kpi-primary .kpi-icon { background: #e2f3f5; color: #21ACCD; }
                .kpi-success .kpi-icon { background: #ecfdf5; color: #10b981; }
                .kpi-danger .kpi-icon { background: #fef2f2; color: #ef4444; }
                .kpi-warning .kpi-icon { background: #fffbeb; color: #f59e0b; }
                .kpi-content { flex: 1; }
                .kpi-label { font-size: 0.7rem; text-transform: uppercase; color: #6c757d; font-weight: 600; letter-spacing: 0.5px; }
                .kpi-value { font-size: 1.3rem; font-weight: 700; margin: 0.25rem 0; color: #1a3c5e; }
                .kpi-unit { font-size: 0.7rem; color: #6c757d; }
                .small-stats-icon { width: 48px; height: 48px; margin: 0 auto; display: flex; align-items: center; justify-content: center; background: rgba(0,0,0,0.05); border-radius: 50%; }
                .reference-badge { background: #f8f9fa; padding: 0.2rem 0.6rem; border-radius: 6px; font-size: 0.7rem; font-family: monospace; }
                .status-badge { display: inline-flex; align-items: center; padding: 0.25rem 0.75rem; border-radius: 20px; font-size: 0.7rem; font-weight: 500; }
                .fs-20 { font-size: 1.25rem; } .fs-24 { font-size: 1.5rem; } .fs-48 { font-size: 3rem; }
                .rounded-3 { border-radius: 12px !important; }
            `}</style>
        </>
    );
}